import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, ImageOff, KeyRound, RefreshCw, Trash2, Upload, Webhook } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { SyncDetails } from "@/components/admin/SyncSummary";
import { InventoryGuide } from "@/components/admin/InventoryGuide";

const card = "rounded-2xl border border-black/10 bg-white p-5";
const inp = "h-10 w-full rounded-lg border border-black/15 bg-white px-3 text-sm outline-none focus:border-amber-500";
const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copiato"));
const H = ({ icon: Icon, children }) => <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-bold"><Icon className="h-5 w-5 text-cyan-700" />{children}</h3>;

const Keys = () => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const load = () => api.get("/admin/inventory/keys").then((r) => setKeys(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/inventory/keys", { name });
      setFresh(data.key); setName(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revocare la chiave "${k.name}"? Il programma che la usa smetterà di sincronizzare.`)) return;
    await api.delete(`/admin/inventory/keys/${k.id}`); toast.success("Chiave revocata"); load();
  };
  return (
    <div className={card} data-testid="inv-keys">
      <H icon={KeyRound}>Chiavi di accesso</H>
      <form onSubmit={create} className="flex gap-2">
        <input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome (es. Gestionale negozio)" className={inp} data-testid="inv-key-name" />
        <button className="shrink-0 rounded-full bg-amber-500 px-4 text-sm font-bold text-black" data-testid="inv-key-create">Genera chiave</button>
      </form>
      {fresh && (
        <div className="mt-3 rounded-xl border-2 border-amber-500 bg-amber-500/10 p-3 text-sm" data-testid="inv-key-fresh">
          <p className="font-semibold">Copia ora la chiave: per sicurezza non verrà più mostrata.</p>
          <div className="mt-2 flex items-center gap-2"><code className="flex-1 break-all rounded bg-white px-2 py-1 text-xs" data-no-translate data-testid="inv-key-value">{fresh}</code>
            <button onClick={() => copy(fresh)} className="rounded-full border border-black/15 p-2" data-testid="inv-key-copy"><Copy className="h-4 w-4" /></button></div>
        </div>
      )}
      <ul className="mt-3 divide-y divide-black/5 text-sm">
        {keys.map((k) => (
          <li key={k.id} className="flex items-center gap-3 py-2" data-testid={`inv-key-${k.id}`}>
            <span className="font-semibold">{k.name}</span><code className="text-xs text-stone-500" data-no-translate>{k.prefix}…</code>
            <span className="ml-auto text-xs text-stone-500">{k.last_used_at ? `Ultimo uso ${fmtDate(k.last_used_at.slice(0, 10))}` : "Mai usata"}</span>
            <button onClick={() => revoke(k)} className="p-1 hover:text-rose-500" data-testid={`inv-key-revoke-${k.id}`}><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
        {!keys.length && <li className="py-2 text-stone-500">Nessuna chiave attiva.</li>}
      </ul>
    </div>
  );
};

const downloadErrors = async (jid) => {
  const { data } = await api.get(`/admin/inventory/import/${jid}/errors.csv`, { responseType: "blob" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "errori-importazione.csv"; a.click();
};

const Report = ({ rep }) => {
  const nErr = rep.error_count ?? rep.errors.length;
  return (
  <div className="mt-4 space-y-2 text-sm" data-testid="inv-report">
    <p className="font-semibold">{rep.dry_run ? "Anteprima (nessuna modifica salvata): " : "Fatto: "}
      <span data-testid="inv-report-created">{rep.created} nuovi</span> · <span data-testid="inv-report-updated">{rep.updated} aggiornati</span> · <span data-testid="inv-report-unchanged">{rep.unchanged} invariati</span> · <span className={nErr ? "text-rose-600" : ""} data-testid="inv-report-errors">{nErr} errori</span>
      {rep.images_queued > 0 && <span className="text-stone-600"> · {rep.images_queued} foto {rep.dry_run ? "da scaricare" : "in scaricamento"}</span>}</p>
    {nErr > 0 && rep.job_id && <button onClick={() => downloadErrors(rep.job_id)} className="flex items-center gap-1 text-xs font-semibold text-rose-700 underline" data-testid="inv-errors-download"><Download className="h-3.5 w-3.5" />Scarica il report errori (CSV)</button>}
    {!!rep.changes.length && (
      <div className="max-h-60 overflow-auto rounded-lg border border-black/10"><table className="w-full text-xs"><tbody>
        {rep.changes.map((c, i) => <tr key={i} className="border-t border-black/5"><td className="p-1.5 text-stone-500">{c.row}</td><td className="p-1.5 font-mono" data-no-translate>{c.code}</td><td className="p-1.5">{c.name}</td><td className="p-1.5 uppercase">{c.channel}</td><td className="p-1.5">{c.action}</td>
          <td className="p-1.5 font-mono">{c.stock[0] ?? "–"} → {c.stock[1]} pz</td><td className="p-1.5 font-mono">{c.price[0] ?? "–"} → {c.price[1]} €</td></tr>)}
      </tbody></table></div>
    )}
    {!!rep.errors.length && <ul className="max-h-40 overflow-auto rounded-lg bg-rose-500/5 p-2 text-xs text-rose-700">{rep.errors.map((e, i) => <li key={i}>Riga {e.row}{e.code ? ` (${e.code})` : ""}: {e.error}</li>)}</ul>}
  </div>
  );
};

const FileImport = ({ onDone }) => {
  const [file, setFile] = useState(null);
  const [create, setCreate] = useState(false);
  const [rep, setRep] = useState(null);
  const [job, setJob] = useState(null);
  const busy = job && !["done", "error"].includes(job.status);
  const run = async (dry) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("create_missing", create); fd.append("dry_run", dry);
    try {
      const { data } = await api.post("/admin/inventory/import", fd);
      setJob({ id: data.job_id, status: "queued", done: 0, total: 0 });
      for (;;) {
        await new Promise((r) => setTimeout(r, 1000));
        const { data: j } = await api.get(`/admin/inventory/import/${data.job_id}`);
        setJob(j);
        if (j.status === "error") { toast.error(j.error); return; }
        if (j.status === "done") { setRep({ ...j.report, job_id: j.id }); if (!dry) { toast.success("Inventario importato"); onDone(); } return; }
      }
    } catch (err) { toast.error(formatApiError(err)); setJob(null); }
  };
  const template = async () => {
    const { data } = await api.get("/admin/inventory/template.csv", { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "modello-inventario-cardsource.csv"; a.click();
  };
  return (
    <div className={card} data-testid="inv-import">
      <H icon={Upload}>Importa un file</H>
      <div className="flex flex-wrap items-center gap-3">
        <input type="file" accept=".csv,.xlsx" onChange={(e) => { setFile(e.target.files[0]); setRep(null); }} className="text-sm" data-testid="inv-file" />
        <button onClick={template} className="flex items-center gap-1 text-sm text-cyan-700" data-testid="inv-template"><Download className="h-4 w-4" />Scarica il modello</button>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={create} onChange={(e) => setCreate(e.target.checked)} data-testid="inv-create-missing" />Crea i prodotti che non esistono sul sito (nascosti, da completare)</label>
      <div className="mt-3 flex gap-2">
        <button disabled={!file || busy} onClick={() => run(true)} className="rounded-full border-2 border-black px-4 py-2 text-sm font-semibold disabled:opacity-40" data-testid="inv-preview">Anteprima</button>
        <button disabled={!file || busy || !rep?.dry_run} onClick={() => run(false)} className="rounded-full bg-amber-500 px-4 py-2 text-sm font-bold text-black disabled:opacity-40" data-testid="inv-apply">Applica le modifiche</button>
      </div>
      {busy && (
        <div className="mt-4" data-testid="inv-progress">
          <div className="h-2 overflow-hidden rounded-full bg-black/10"><div className="h-full bg-amber-500 transition-[width]" style={{ width: `${job.total ? (job.done / job.total) * 100 : 3}%` }} data-testid="inv-progress-bar" /></div>
          <p className="mt-1 text-xs text-stone-600" data-testid="inv-progress-text">{job.phase || "In coda"}{job.total ? ` · ${job.done} di ${job.total} righe` : "..."}</p>
        </div>
      )}
      {rep && !busy && <Report rep={rep} />}
    </div>
  );
};

const Outbound = ({ ov, reload }) => {
  const [url, setUrl] = useState(ov.webhook_url || "");
  const save = async () => {
    try { await api.put("/admin/inventory/settings", { webhook_url: url || null }); toast.success("Impostazioni salvate"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const test = async () => {
    const { data } = await api.post("/admin/inventory/webhook-test");
    data.ok ? toast.success("Il tuo inventario ha risposto correttamente") : toast.error(`Nessuna risposta valida (${data.status || data.error})`);
  };
  const rotate = async () => {
    if (!window.confirm("Generare un nuovo segreto di firma? Dovrai aggiornarlo anche nel tuo inventario.")) return;
    await api.post("/admin/inventory/settings/rotate-secret"); reload();
  };
  const retry = async () => { const { data } = await api.post("/admin/inventory/movements/retry"); toast.success(`${data.scheduled} invii in coda`); };
  return (
    <div className={card} data-testid="inv-outbound">
      <H icon={Webhook}>Vendite online → inventario</H>
      <label className="text-xs text-stone-600">Indirizzo del tuo inventario che riceve le vendite (https)</label>
      <div className="mt-1 flex gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://mio-inventario.it/webhook/cardsource" className={inp} data-testid="inv-webhook-url" />
        <button onClick={save} className="shrink-0 rounded-full bg-amber-500 px-4 text-sm font-bold text-black" data-testid="inv-webhook-save">Salva</button>
      </div>
      {ov.webhook_secret && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-stone-600">Segreto di firma:</span><code className="rounded bg-black/5 px-2 py-0.5 text-xs" data-no-translate data-testid="inv-webhook-secret">{ov.webhook_secret}</code>
          <button onClick={() => copy(ov.webhook_secret)} className="p-1" data-testid="inv-secret-copy"><Copy className="h-4 w-4" /></button>
          <button onClick={rotate} className="flex items-center gap-1 text-xs text-stone-500" data-testid="inv-secret-rotate"><RefreshCw className="h-3.5 w-3.5" />Rigenera</button>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <button disabled={!ov.webhook_url} onClick={test} className="rounded-full border-2 border-black px-4 py-1.5 text-sm font-semibold disabled:opacity-40" data-testid="inv-webhook-test">Invia un test</button>
        {ov.pending > 0 && <button onClick={retry} className="rounded-full border border-rose-400 px-4 py-1.5 text-sm text-rose-600" data-testid="inv-retry">Ritenta {ov.pending} invii non consegnati</button>}
      </div>
      <p className="mt-3 text-xs text-stone-500">Senza indirizzo le vendite restano comunque disponibili: il tuo inventario può leggerle quando vuole (vedi istruzioni, punto 4).</p>
    </div>
  );
};

const History = ({ ov }) => (
  <div className={card} data-testid="inv-history">
    <H icon={RefreshCw}>Ultime attività</H>
    <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Sincronizzazioni</p>
    <ul className="mb-4 mt-1 text-sm">{ov.syncs.map((s) => <li key={s.id} className="border-t border-black/5 py-1.5" data-testid={`inv-sync-${s.id}`}><span className="flex gap-3"><span className="text-stone-500">{fmtDate(s.at.slice(0, 10))}</span><span data-no-translate>{s.source}</span><span className="ml-auto">{s.updated} agg. · {s.created} nuovi · {s.errors.length} errori</span></span><SyncDetails s={s} testid={`inv-sync-details-${s.id}`} /></li>)}
      {!ov.syncs.length && <li className="text-stone-500">Ancora nessuna.</li>}</ul>
    <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Movimenti inviati all'inventario</p>
    <ul className="mt-1 text-sm">{ov.movements.map((m) => <li key={m.id} className="flex gap-3 border-t border-black/5 py-1.5"><span className="font-mono text-xs">#{m.seq}</span><span>{m.type}</span><span data-no-translate>{m.order_number}</span>
      <span className={`ml-auto text-xs ${m.delivered_at ? "text-teal-700" : "text-stone-500"}`}>{m.delivered_at ? "consegnato" : ov.webhook_url ? (m.last_error ? `in attesa (${m.last_error})` : "in attesa") : "da leggere"}</span></li>)}
      {!ov.movements.length && <li className="text-stone-500">Ancora nessuno.</li>}</ul>
  </div>
);

const ImageErrors = () => {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/inventory/image-errors").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const retry = async (ids) => {
    const { data } = await api.post("/admin/inventory/image-errors/retry", { ids });
    toast.success(`${data.scheduled} foto in nuovo scaricamento`, { description: "Aggiorna l'elenco tra qualche secondo" });
    setTimeout(load, 5000);
  };
  if (!d) return null;
  return (
    <div className={card} data-testid="inv-image-errors">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <H icon={ImageOff}>Foto dal gestionale non scaricate</H>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 text-xs text-stone-500" data-testid="inv-image-errors-refresh"><RefreshCw className="h-3.5 w-3.5" />Aggiorna</button>
          {d.count > 0 && <button onClick={() => retry(null)} className="rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-black" data-testid="inv-image-errors-retry-all">Riprova tutte ({d.count})</button>}
        </div>
      </div>
      {d.count === 0 ? <p className="text-sm text-stone-500" data-testid="inv-image-errors-empty">Nessun problema: tutte le foto inviate dal gestionale sono state scaricate.</p> : (
        <ul className="max-h-80 divide-y divide-black/5 overflow-auto text-sm" data-testid="inv-image-errors-list">
          {d.items.map((p) => (
            <li key={p.id} className="flex flex-wrap items-start gap-3 py-2" data-testid={`inv-image-error-${p.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{p.name} {p.sku && <span className="font-mono text-xs text-stone-500" data-no-translate>{p.sku}</span>}</p>
                <p className="text-rose-700" data-testid={`inv-image-error-reason-${p.id}`}>{p.image_error.reason}</p>
                <a href={p.image_error.url} target="_blank" rel="noreferrer" className="block truncate text-xs text-cyan-700 underline" data-no-translate>{p.image_error.url}</a>
                <p className="text-xs text-stone-500">{fmtDate(p.image_error.at.slice(0, 10))}</p>
              </div>
              <button onClick={() => retry([p.id])} className="rounded-full border border-black/20 px-3 py-1 text-xs" data-testid={`inv-image-error-retry-${p.id}`}>Riprova</button>
            </li>
          ))}
          {d.count > d.items.length && <li className="py-2 text-xs text-stone-500">…e altri {d.count - d.items.length}</li>}
        </ul>
      )}
    </div>
  );
};

export const AdminInventory = () => {
  const [ov, setOv] = useState(null);
  const load = () => api.get("/admin/inventory/overview").then((r) => setOv(r.data));
  useEffect(() => { load(); }, []);
  if (!ov) return <p className="py-8 text-stone-500">Caricamento...</p>;
  return (
    <div className="space-y-5" data-testid="admin-inventory">
      <div className="flex flex-wrap gap-3 text-sm">
        <span className="rounded-full bg-black/5 px-3 py-1" data-testid="inv-with-code">{ov.with_code} di {ov.products} prodotti con SKU/EAN</span>
        {ov.hidden > 0 && <span className="rounded-full bg-amber-500/15 px-3 py-1" data-testid="inv-hidden">{ov.hidden} prodotti nascosti da completare</span>}
      </div>
      <InventoryGuide />
      <div className="grid gap-5 lg:grid-cols-2"><Keys /><Outbound ov={ov} reload={load} /></div>
      <History ov={ov} />
      <ImageErrors />
    </div>
  );
};
